import React, { useState, useContext, useEffect, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper, Button, Grid, Typography, Chip, Divider, LinearProgress,
  FormControl, InputLabel, Select, MenuItem, Box, Card, CardContent,
} from "@material-ui/core";
import {
  CheckCircle, Cancel, CalendarToday, TrendingUp, Star,
} from "@material-ui/icons";

import SubscriptionModal from "../../components/SubscriptionModal";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainContainer from "../../components/MainContainer";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import { formatPlanDisplayName } from "../../utils/formatPlanDisplayName";

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(1) },
  planCard: {
    borderRadius: 16,
    background: "linear-gradient(135deg, #1e3a5f 0%, #00c9a7 100%)",
    color: "#fff",
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    position: "relative",
    overflow: "hidden",
    "&::after": {
      content: '""', position: "absolute", top: -40, right: -40,
      width: 120, height: 120, borderRadius: "50%",
      background: "rgba(255,255,255,0.08)",
    },
  },
  planName: { fontSize: "1.8rem", fontWeight: 800, letterSpacing: "-0.02em" },
  planPrice: { fontSize: "2.4rem", fontWeight: 800, marginTop: 4 },
  planPriceUnit: { fontSize: "1rem", fontWeight: 400, opacity: 0.7 },
  sectionCard: {
    borderRadius: 12,
    marginBottom: theme.spacing(2),
    border: "1px solid #e0e0e0",
  },
  sectionTitle: {
    fontWeight: 700, fontSize: "0.95rem", color: theme.palette.text.secondary,
    textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 12,
  },
  tokenBar: {
    height: 12, borderRadius: 6, backgroundColor: "#e8f5e9",
    "& .MuiLinearProgress-bar": { borderRadius: 6, background: "linear-gradient(90deg, #43a047, #66bb6a)" },
  },
  tokenBarWarning: {
    "& .MuiLinearProgress-bar": { background: "linear-gradient(90deg, #ef6c00, #ff9800)" },
  },
  featureChip: { margin: 2, fontWeight: 600, fontSize: "0.78rem" },
  upgradeCard: {
    borderRadius: 12,
    border: "2px solid " + theme.palette.primary.main,
    background: theme.palette.primary.main + "08",
  },
  calendarBtn: {
    background: "#4285F4", color: "#fff", borderRadius: 8, fontWeight: 600,
    textTransform: "none", padding: "10px 24px",
    "&:hover": { background: "#3367d6" },
  },
  trialBanner: {
    borderRadius: 12, padding: theme.spacing(2),
    background: "linear-gradient(135deg, #ff9800 0%, #f57c00 100%)",
    color: "#fff", marginBottom: theme.spacing(2),
    display: "flex", alignItems: "center", gap: 12,
  },
}));

const Subscription = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const { getPlanCompany, getPlanList } = usePlans();

  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [planCompany, setPlanCompany] = useState(null);
  const [publicPlans, setPublicPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [upgrading, setUpgrading] = useState(false);
  const [membership, setMembership] = useState(null);
  const [calendarStatus, setCalendarStatus] = useState(null);

  useEffect(() => {
    if (!user?.companyId) return;
    const load = async () => {
      setPlansLoading(true);
      try {
        const pd = await getPlanCompany(undefined, user.companyId);
        setPlanCompany(pd || null);
        const pl = await getPlanList();
        setPublicPlans(Array.isArray(pl) ? pl : []);
      } catch (e) { toastError(e); }
      finally { setPlansLoading(false); }

      try { const { data } = await api.get("/membership"); setMembership(data); } catch {}
      try { const { data } = await api.get("/integrations/google-calendar/status"); setCalendarStatus(data); } catch {}
    };
    load();
  }, [user?.companyId]);

  const currentAmount = Number(planCompany?.plan?.amount || 0);
  const sortedPlans = useMemo(() => {
    const l = Array.isArray(publicPlans) ? [...publicPlans] : [];
    l.sort((a, b) => Number(a.amount || 0) - Number(b.amount || 0));
    return l;
  }, [publicPlans]);

  useEffect(() => {
    if (!sortedPlans.length) return;
    const up = sortedPlans.find((p) => Number(p.amount || 0) > currentAmount);
    if (up) setSelectedPlanId(up.id);
    else if (planCompany?.plan?.id) setSelectedPlanId(planCompany.plan.id);
  }, [sortedPlans, currentAmount]);

  const canUpgrade = !!sortedPlans.find(p => String(p.id) === String(selectedPlanId)) &&
    Number(sortedPlans.find(p => String(p.id) === String(selectedPlanId))?.amount || 0) > currentAmount && !upgrading;

  const handleUpgrade = async () => {
    if (!user?.companyId || !selectedPlanId) return;
    setUpgrading(true);
    try {
      await api.put(`/companies/${user.companyId}/plan`, { planId: selectedPlanId });
      toast.success("Plan actualizado correctamente");
      const r = await getPlanCompany(undefined, user.companyId);
      setPlanCompany(r || null);
    } catch (e) { toastError(e); }
    finally { setUpgrading(false); }
  };

  const handleConnectCalendar = async () => {
    try { const { data } = await api.get("/integrations/google-calendar/auth-url"); if (data.authUrl) window.location.href = data.authUrl; }
    catch (e) { toastError(e); }
  };
  const handleDisconnectCalendar = async () => {
    try { await api.delete("/integrations/google-calendar/disconnect"); setCalendarStatus({ connected: false }); toast.success("Desconectado"); }
    catch (e) { toastError(e); }
  };

  const ai = membership?.ai;
  const feat = membership?.features;
  const trial = membership?.trial;
  const plan = membership?.plan;

  const FEATURES = [
    { key: "whatsapp", label: "WhatsApp", color: "#25d366" },
    { key: "facebook", label: "Facebook", color: "#1877f2" },
    { key: "instagram", label: "Instagram", color: "#e4405f" },
    { key: "googleCalendar", label: "Google Calendar", color: "#4285F4" },
    { key: "campaigns", label: "Campanas", color: "#ff9800" },
    { key: "openAi", label: "IA Claude", color: "#6b52ae" },
    { key: "kanban", label: "Kanban", color: "#00bcd4" },
    { key: "externalApi", label: "API REST", color: "#607d8b" },
    { key: "internalChat", label: "Chat Interno", color: "#795548" },
    { key: "integrations", label: "Integraciones", color: "#9e9e9e" },
    { key: "schedules", label: "Agendamiento", color: "#8bc34a" },
  ];

  return (
    <MainContainer style={{ overflowY: "auto" }}>
      <SubscriptionModal open={contactModalOpen} onClose={() => setContactModalOpen(false)} contactId={null} />
      <MainHeader><Title>Membresia</Title></MainHeader>

      <Grid container spacing={2} style={{ maxWidth: 900, margin: "0 auto" }}>

        {/* === TRIAL BANNER === */}
        {trial?.isTrial && (
          <Grid item xs={12}>
            <div className={classes.trialBanner}>
              <Star style={{ fontSize: 32 }} />
              <div>
                <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                  Periodo de prueba: {trial.trialDays} dias restantes
                </Typography>
                <Typography variant="body2" style={{ opacity: 0.9 }}>
                  Tu demo termina el {trial.trialEnds?.substring(0, 10)}. Suscribete para no perder acceso.
                </Typography>
              </div>
            </div>
          </Grid>
        )}

        {/* === PLAN CARD === */}
        <Grid item xs={12}>
          <div className={classes.planCard}>
            <Typography variant="overline" style={{ opacity: 0.7, letterSpacing: "0.15em" }}>TU PLAN ACTUAL</Typography>
            <Typography className={classes.planName}>{plan?.name || "Sin plan"}</Typography>
            <Typography className={classes.planPrice}>
              USD ${plan?.amount || 0}<span className={classes.planPriceUnit}>/mes</span>
            </Typography>
            <Typography variant="body2" style={{ opacity: 0.7, marginTop: 4 }}>
              {plan?.description || ""}
            </Typography>
            <div style={{ display: "flex", gap: 16, marginTop: 16 }}>
              <div><Typography variant="h5" style={{ fontWeight: 700 }}>{membership?.limits?.usersUsed || 0}/{membership?.limits?.users || 0}</Typography><Typography variant="caption" style={{ opacity: 0.7 }}>Agentes</Typography></div>
              <div><Typography variant="h5" style={{ fontWeight: 700 }}>{membership?.limits?.contactsUsed || 0}/{membership?.limits?.contacts || 0}</Typography><Typography variant="caption" style={{ opacity: 0.7 }}>Contactos</Typography></div>
              <div><Typography variant="h5" style={{ fontWeight: 700 }}>{membership?.limits?.connections || 0}</Typography><Typography variant="caption" style={{ opacity: 0.7 }}>Conexiones</Typography></div>
            </div>
          </div>
        </Grid>

        {/* === TOKENS IA === */}
        {ai && (
          <Grid item xs={12} md={6}>
            <Card className={classes.sectionCard} variant="outlined">
              <CardContent>
                <Typography className={classes.sectionTitle}>
                  <TrendingUp style={{ fontSize: 18, verticalAlign: "middle", marginRight: 6 }} />
                  Tokens IA del mes
                </Typography>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <Typography variant="h4" style={{ fontWeight: 800, color: ai.percentUsed >= 80 ? "#ef6c00" : "#43a047" }}>
                    {ai.tokensRemaining.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" style={{ alignSelf: "flex-end" }}>
                    de {ai.tokensIncluded.toLocaleString()}
                  </Typography>
                </div>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(ai.percentUsed, 100)}
                  className={`${classes.tokenBar} ${ai.percentUsed >= 80 ? classes.tokenBarWarning : ""}`}
                />
                <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                  <Chip size="small" label={`Modelo: ${(ai.model || "sonnet").toUpperCase()}`} className={classes.featureChip} style={{ background: "#e8eaf6", color: "#3949ab" }} />
                  <Chip size="small" label={`Extra: S/${ai.extraTokenPrice}/token`} className={classes.featureChip} />
                  {ai.percentUsed >= 80 && <Chip size="small" color="secondary" label="Por agotarse" className={classes.featureChip} />}
                </div>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* === GOOGLE CALENDAR === */}
        {feat?.googleCalendar && (
          <Grid item xs={12} md={6}>
            <Card className={classes.sectionCard} variant="outlined">
              <CardContent>
                <Typography className={classes.sectionTitle}>
                  <CalendarToday style={{ fontSize: 18, verticalAlign: "middle", marginRight: 6 }} />
                  Google Calendar
                </Typography>
                {calendarStatus?.connected ? (
                  <div>
                    <Chip color="primary" label={`Conectado: ${calendarStatus.email}`} style={{ marginBottom: 12 }} />
                    <br />
                    <Button variant="outlined" size="small" onClick={handleDisconnectCalendar}>Desconectar</Button>
                  </div>
                ) : (
                  <div>
                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
                      Conecta tu Google Calendar para que tus clientes agenden citas por WhatsApp.
                    </Typography>
                    <Button className={classes.calendarBtn} onClick={handleConnectCalendar} startIcon={<CalendarToday />}>
                      Conectar Google Calendar
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* === FEATURES === */}
        {feat && (
          <Grid item xs={12}>
            <Card className={classes.sectionCard} variant="outlined">
              <CardContent>
                <Typography className={classes.sectionTitle}>Incluido en tu plan</Typography>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {FEATURES.map(f => (
                    <Chip
                      key={f.key}
                      size="small"
                      icon={feat[f.key] ? <CheckCircle style={{ color: "#fff" }} /> : <Cancel style={{ color: "#bbb" }} />}
                      label={f.label}
                      className={classes.featureChip}
                      style={feat[f.key]
                        ? { backgroundColor: f.color, color: "#fff" }
                        : { backgroundColor: "#f5f5f5", color: "#999", textDecoration: "line-through" }
                      }
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* === UPGRADE === */}
        <Grid item xs={12}>
          <Card className={classes.upgradeCard} variant="outlined">
            <CardContent>
              <Typography className={classes.sectionTitle} style={{ color: "#1e3a5f" }}>
                <Star style={{ fontSize: 18, verticalAlign: "middle", marginRight: 6, color: "#1e3a5f" }} />
                Mejorar plan
              </Typography>
              <FormControl variant="outlined" fullWidth margin="dense">
                <InputLabel>Seleccionar plan</InputLabel>
                <Select
                  value={selectedPlanId || ""}
                  onChange={(e) => setSelectedPlanId(e.target.value)}
                  label="Seleccionar plan"
                  disabled={plansLoading || upgrading}
                >
                  {sortedPlans.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name} — USD ${p.amount}/mes — {p.aiTokensIncluded || 0} tokens IA
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <div style={{ display: "flex", gap: 12, marginTop: 16 }}>
                <Button
                  variant="contained" color="primary" fullWidth
                  onClick={handleUpgrade} disabled={!canUpgrade}
                  style={{ borderRadius: 8, fontWeight: 600, textTransform: "none", padding: "12px 0" }}
                >
                  {upgrading ? "Actualizando..." : "Actualizar plan"}
                </Button>
                <Button
                  variant="outlined" color="primary" fullWidth
                  onClick={() => window.location.href = "/checkout"}
                  style={{ borderRadius: 8, fontWeight: 600, textTransform: "none", padding: "12px 0" }}
                >
                  Ir a pagar
                </Button>
              </div>
            </CardContent>
          </Card>
        </Grid>

      </Grid>
    </MainContainer>
  );
};

export default Subscription;
